const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  optionValueSelectionChange: 'option-value-selection-change',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

// Nachtcomfort: show configurator extras under the main bed in cart,
// while keeping their technical cart lines for correct price calculation.
(() => {
  if (window.__ncCartConfigGroupingBound) return;
  window.__ncCartConfigGroupingBound = true;

  const isAddon = (item) => String(item?.properties?._nc_addon || '').toLowerCase() === 'true';
  const productTitle = (item) => String(item?.product_title || item?.title || '').trim();
  const money = (cents) => new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
  }).format((Number(cents) || 0) / 100);

  const findParentIndex = (items, addonIndex, groupTitle) => {
    const wanted = String(groupTitle || '').trim();
    if (!wanted) return -1;

    for (let index = addonIndex - 1; index >= 0; index -= 1) {
      const item = items[index];
      if (!isAddon(item) && productTitle(item) === wanted) return index;
    }

    return items.findIndex((item) => !isAddon(item) && productTitle(item) === wanted);
  };

  const customerLabel = (item) => {
    const choice = String(item?.properties?._Keuze || '').trim();
    const title = productTitle(item);
    const combined = `${choice} ${title}`.toLowerCase();

    if (combined.includes('nachtkast')) {
      const quantity = Math.max(1, Number(item?.quantity) || 1);
      return quantity > 1 ? `Bijpassend nachtkastje × ${quantity}` : 'Bijpassend nachtkastje';
    }
    if (choice) return choice;
    if (title.toLowerCase().includes('all-in service')) return 'Onderhoudspakket + 5 jaar service & garantie';
    return title || 'Extra';
  };

  const buildGroups = (items) => {
    const groups = new Map();
    const handledAddonIndexes = new Set();

    items.forEach((item, addonIndex) => {
      if (!isAddon(item)) return;

      const parentIndex = findParentIndex(items, addonIndex, item.properties?.['_Configuratie bij']);
      if (parentIndex < 0) return;

      if (!groups.has(parentIndex)) groups.set(parentIndex, []);
      groups.get(parentIndex).push({
        label: customerLabel(item),
        price: Number(item.final_line_price ?? item.line_price ?? 0),
      });
      handledAddonIndexes.add(addonIndex);
    });

    return { groups, handledAddonIndexes };
  };

  const restoreOurChanges = (row) => {
    if (!row) return;
    if (row.dataset.ncAddonHidden === 'true') {
      row.hidden = false;
      row.style.removeProperty('display');
      delete row.dataset.ncAddonHidden;
    }
    row.querySelectorAll('[data-nc-config-duplicate-hidden="true"]').forEach((element) => {
      element.style.removeProperty('display');
      element.removeAttribute('data-nc-config-duplicate-hidden');
    });
    row.querySelectorAll('.nc-cart-config-extras').forEach((element) => element.remove());
  };

  const duplicateKeysFor = (extras) => {
    const keys = new Set();
    extras.forEach(({ label }) => {
      const normalized = String(label || '').toLowerCase();
      if (normalized.includes('nachtkast')) keys.add('Nachtkastjes');
      if (normalized.includes('onderhoudspakket')) keys.add('Onderhoudspakket');
      if (normalized.includes('oude bed')) {
        keys.add('Oude bed meenemen & afvoeren');
        keys.add('Oude bed afvoeren');
      }
    });
    return keys;
  };

  const renderScope = (cart, prefix, grouping) => {
    const items = cart.items || [];

    items.forEach((item, index) => {
      const rowId = prefix ? `CartDrawer-Item-${index + 1}` : `CartItem-${index + 1}`;
      restoreOurChanges(document.getElementById(rowId));
    });

    grouping.handledAddonIndexes.forEach((index) => {
      const rowId = prefix ? `CartDrawer-Item-${index + 1}` : `CartItem-${index + 1}`;
      const row = document.getElementById(rowId);
      if (!row) return;
      row.dataset.ncAddonHidden = 'true';
      row.hidden = true;
      row.style.setProperty('display', 'none', 'important');
    });

    grouping.groups.forEach((extras, parentIndex) => {
      const rowId = prefix ? `CartDrawer-Item-${parentIndex + 1}` : `CartItem-${parentIndex + 1}`;
      const row = document.getElementById(rowId);
      const details = row?.querySelector('.cart-item__details');
      if (!details) return;

      const duplicateKeys = duplicateKeysFor(extras);
      details.querySelectorAll('.product-option').forEach((option) => {
        const key = String(option.querySelector('dt')?.textContent || '').replace(/:\s*$/, '').trim();
        if (!duplicateKeys.has(key)) return;
        option.dataset.ncConfigDuplicateHidden = 'true';
        option.style.setProperty('display', 'none', 'important');
      });

      const box = document.createElement('div');
      box.className = 'nc-cart-config-extras';

      const heading = document.createElement('strong');
      heading.className = 'nc-cart-config-extras__title';
      heading.textContent = "Gekozen extra's";
      box.appendChild(heading);

      extras.forEach((extra) => {
        const line = document.createElement('div');
        line.className = 'nc-cart-config-extras__line';

        const label = document.createElement('span');
        label.textContent = extra.label;

        const price = document.createElement('span');
        price.className = 'nc-cart-config-extras__price';
        price.textContent = `+ ${money(extra.price)}`;

        line.append(label, price);
        box.appendChild(line);
      });

      details.appendChild(box);
    });
  };

  let refreshTimer;
  let refreshRunning = false;

  const refresh = async () => {
    if (refreshRunning) return;
    refreshRunning = true;

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) return;

      const cart = await response.json();
      const grouping = buildGroups(cart.items || []);
      renderScope(cart, '', grouping);
      renderScope(cart, 'CartDrawer-', grouping);
    } catch (error) {
      console.error('[Nachtcomfort] Winkelwagenconfiguratie kon niet worden gegroepeerd.', error);
    } finally {
      refreshRunning = false;
    }
  };

  const scheduleRefresh = () => {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refresh, 90);
  };

  const style = document.createElement('style');
  style.id = 'nc-cart-config-extras-style';
  style.textContent = `
    .nc-cart-config-extras {
      margin-top: 1rem;
      padding-top: .8rem;
      border-top: 1px solid rgba(var(--color-foreground), .14);
    }
    .nc-cart-config-extras__title {
      display: block;
      margin-bottom: .35rem;
      font-size: 1.3rem;
      line-height: 1.4;
    }
    .nc-cart-config-extras__line {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 1rem;
      font-size: 1.2rem;
      line-height: 1.45;
    }
    .nc-cart-config-extras__price {
      white-space: nowrap;
      font-weight: 600;
    }
  `;
  document.head.appendChild(style);

  document.addEventListener('DOMContentLoaded', scheduleRefresh, { once: true });
  document.addEventListener('cart-update', scheduleRefresh);
  document.addEventListener('quantity-update', scheduleRefresh);
  document.addEventListener('shopify:section:load', scheduleRefresh);

  const observer = new MutationObserver((mutations) => {
    const cartChanged = mutations.some((mutation) => [...mutation.addedNodes].some((node) => {
      if (node.nodeType !== 1) return false;
      return node.matches?.('.cart-item, #main-cart-items, #CartDrawer-CartItems, cart-items, cart-drawer-items')
        || node.querySelector?.('.cart-item, #main-cart-items, #CartDrawer-CartItems, cart-items, cart-drawer-items');
    }));
    if (cartChanged) scheduleRefresh();
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
  scheduleRefresh();
})();
